// Modul obstarává zobrazování a donačítání recombee v seznamech článků

import * as Sentry from '@sentry/browser';
import * as RecombeeJSAPIClient from 'recombee-js-api-client';

import { CNCRecombee } from '@customTypes/recombee/CNCRecombee';
import { RenderButtonLoadMore, RecombeeClient as client, MovePRStandard } from '@components';
import * as recombeeConfig from '@utils/recombee';

import { CLASS_BTN, CLASS_LIST_WRAPPER } from './recombeeElementSelectors';
import { recombeeCreateElementsAndRenderDOM as createElementsAndRenderDOM } from './recombeeCreateElementsAndRenderDOM';
import { recombeeLoadMore } from './recombeeLoadMore';
import { increaseButtonLinkParameter, getCurrentPageNumber } from '@utils/loadMore';

export const Recombee = (lists: NodeListOf<HTMLDivElement>) => {
  // GTM
  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({
      page: {
        pagination: {
          type: 'more',
          page: 1,
        },
      },
    });
  }

  // Načíst inicializaci z TPL
  if (!window.CNC.recombee || !window.CNC.recombee.box) return;
  const CNCRecombeeWindowConfig = window.CNC.recombee;
  const { userId, articleId: sourceArticleId, box }: CNCRecombee.General = CNCRecombeeWindowConfig;

  const loadRecommbeData = () => {
    lists.forEach((list, listIndex) => {
      // Přeskočit všechny již načtené nebo nenačtené boxy, aktuální ve viewportu má stav loading
      if (
        typeof box === 'undefined' ||
        list.dataset.recombeeLoaded === 'unloaded' ||
        list.dataset.recombeeLoaded === 'loaded'
      )
        return;

      // Načíst fallbacky
      const fallbackArticles: NodeListOf<HTMLDivElement> = list.querySelectorAll('.article-card');
      const articlesCount: number = fallbackArticles.length;

      // Načíst a připravit konfiguraci z boxu v CMS ('Vlastny konfig')
      const recombeeBoxConfig: CNCRecombee.Box = box[listIndex];
      if (!recombeeBoxConfig) return;

      // Stav boxu
      const {
        scenario = 'isport-odjinud',
        endpoint = 'items-to-user',
        buttonText = 'Načíst další články', // Taky vyhodit
      } = recombeeBoxConfig;
      recombeeConfig.request.scenario = scenario;

      // Načíst nativně první várku doporučených Recombee článků
      client
        .send(
          // Na API call dle endpointu
          endpoint === 'items-to-item'
            ? new RecombeeJSAPIClient.RecommendItemsToItem(
                sourceArticleId,
                userId,
                articlesCount,
                recombeeConfig.request
              )
            : new RecombeeJSAPIClient.RecommendItemsToUser(
                userId,
                articlesCount,
                recombeeConfig.request
              )
        )
        .then((response) => {
          // V konkrétním vykresleném 'listArticle' boxu

          // Načíst identifikátor boxu a odpověď
          const resp = response as RecombeeJSAPIClient.RecombeeClientResponse;
          const { recommId } = resp;

          // Odstranit tlačítko pro načtení dalších článků, pokud ho vykreslila šablona.
          const buttonLoadMoreRendered = list.querySelector(`.${CLASS_LIST_WRAPPER}`);
          if (buttonLoadMoreRendered) {
            buttonLoadMoreRendered.remove();
          }

          // Vytvořit elementy a překreslit DOM
          createElementsAndRenderDOM({
            list,
            listIndex,
            replace: true,
            resp,
          });

          // Odstranit název magazínů kvůli přesunutí PR článku na správné místo
          const scenarioSplit = scenario.split('-article-');
          const targetPRMoving = scenarioSplit[1];

          // Přidat nové donačítací tlačítko
          const newButton = RenderButtonLoadMore({ scenario: targetPRMoving, buttonText });
          if (newButton) {
            list.insertAdjacentElement('beforeend', newButton);
          }

          // Po kliknutí nové donačítací tlačítko
          const loadMoreBtn: HTMLLinkElement | null = list.querySelector(`.${CLASS_BTN}`);
          if (loadMoreBtn) {
            loadMoreBtn.addEventListener('click', (event) => {
              let pageNumber = 1;

              if (loadMoreBtn) {
                pageNumber = getCurrentPageNumber(loadMoreBtn);
                // Navýšit +1 parametr 'page' v odkazu tlačítka
                increaseButtonLinkParameter(loadMoreBtn);
              }

              // Načíst další články
              recombeeLoadMore({
                articlesCount,
                event,
                list,
                listIndex,
                pageNumber,
                recommId,
              });
            });
          }

          // Přesunout PR článek/y před tlačítko/a 'Načíst další'
          const articlePRWrapper: HTMLDivElement | null =
            document.querySelector('[data-pr-article]');
          if (articlePRWrapper) MovePRStandard();
        })
        .catch((err) => {
          Sentry.captureException(
            'Recombee: Nenačetly se články z Recombee. Dev hint to fix: zkontroluj network tab, do filtru recombee a podivej se na headers, payload & response' +
              err
          );
        });
    });
  };

  // Nastavení pro Recombee Observer
  const RecombeeIntersectionOptions = {
    root: null,
    rootMargin: '500px',
    threshold: 0,
  };

  // Recombee Observer správa položek
  const handleRecombeeIntersect = (
    entries: IntersectionObserverEntry[],
    RecombeeIntersectionObserver: IntersectionObserver
  ) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        // Nastavení data-recombee-loaded
        const htmlTarget = entry.target as HTMLElement;
        htmlTarget.dataset.recombeeLoaded = 'loading';
        // Načíst data pro box který se dostaly do viewportu
        loadRecommbeData();
        htmlTarget.dataset.recombeeLoaded = 'loaded';
        // Přestat box sledovat
        RecombeeIntersectionObserver.unobserve(entry.target);
      }
    });
  };

  // Recombee Observer
  const RecombeeIntersectionObserver = new IntersectionObserver(
    handleRecombeeIntersect,
    RecombeeIntersectionOptions
  );

  // Pro všechny seznamy článků s 'data-recombee' zapni Observer
  lists.forEach((list) => {
    RecombeeIntersectionObserver.observe(list);
  });
};
